import { isEqualWith } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';

import { shipmentTemplateFormToInput } from '../../../app/data/shipmentTemplateConversions';
import { isFormValuePresent } from '../../../components/forms/formHelpers';

export function useShipmentTemplateChangeDetectionInterceptors() {
  const [checkDiffCounter, setCheckDiffCounter] = useState(0);
  const onBlur = useCallback(
    () => setCheckDiffCounter(checkDiffCounter + 1),
    [checkDiffCounter]
  );
  const onKeyDown = useCallback(
    e => {
      if (e.key === 'Enter') {
        setCheckDiffCounter(checkDiffCounter + 1);
      }
    },
    [checkDiffCounter]
  );
  return { onBlur, onKeyDown, props: { checkDiffCounter } };
}

// Special logic is applied - entering an empty field is not considered as change
function isTemplateChanged(templateValues, currentValues) {
  return !isEqualWith(
    shipmentTemplateFormToInput(templateValues),
    shipmentTemplateFormToInput(currentValues),
    tplVal => {
      if (!isFormValuePresent(tplVal)) {
        return true;
      }
      // undefined signals that the default `isEqual` behaviour should be used
      return undefined;
    }
  );
}

export default function ShipmentTemplatesChangedDialog({
  activeItem,
  setActiveItem,
  values,
  afterApply,
  checkDiffCounter,
}) {
  /**
   * THE PROCESS:
   * - If no template is active, any change is ignored
   * - If a template is active and any change is made (except entering field that was empty in the template),
   *   the template is dismissed and the user's changes are kept
   */

  // Used for reverting
  const lastValues = useRef(values);
  // Used for comparison
  const templateValues = useRef(values);
  // Used to check if the change was caused by form change or template change
  const lastActiveItem = useRef(activeItem);

  useEffect(() => {
    lastValues.current = values;
    templateValues.current = values;
    lastActiveItem.current = activeItem;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItem]);

  useEffect(() => {
    if (
      activeItem &&
      activeItem === lastActiveItem.current &&
      isTemplateChanged(lastValues.current, values)
    ) {
      setActiveItem(undefined);
      afterApply();
    }
    lastValues.current = values;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkDiffCounter]);

  return null;
}
